// PPTX theme injection — Level 1 template export.
//
// dom-to-pptx produces a flat .pptx with hard-coded colors/fonts and no
// brand metadata. This utility post-processes the exported blob to inject
// a proper theme1.xml (color scheme + font scheme) so that opening the file
// in PowerPoint shows the brand palette under Design → Variants → Colors
// and the brand fonts under Design → Variants → Fonts.
//
// This does NOT rebind existing slide elements to theme references — those
// stay as literal values. But "New Slide" inherits the theme, and the file
// now behaves like a real template when a user picks it up.

import JSZip from 'jszip';
import { withBackend } from './api';
import { authHeaders } from './authHeaders';

// ─────────────────────────────────────────────────────────────────────
// Theme XML template
// ─────────────────────────────────────────────────────────────────────
// Placeholders: {{NAME}}, {{DK1}}, {{LT1}}, {{DK2}}, {{LT2}},
// {{ACCENT1..6}}, {{MAJOR_FONT}}, {{MINOR_FONT}}.
//
// The fmtScheme block is the standard Office default — PowerPoint refuses
// to open the file if it's missing or malformed, so we keep it verbatim.
const THEME_XML_TEMPLATE = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" name="{{NAME}}">
  <a:themeElements>
    <a:clrScheme name="{{NAME}}">
      <a:dk1><a:srgbClr val="{{DK1}}"/></a:dk1>
      <a:lt1><a:srgbClr val="{{LT1}}"/></a:lt1>
      <a:dk2><a:srgbClr val="{{DK2}}"/></a:dk2>
      <a:lt2><a:srgbClr val="{{LT2}}"/></a:lt2>
      <a:accent1><a:srgbClr val="{{ACCENT1}}"/></a:accent1>
      <a:accent2><a:srgbClr val="{{ACCENT2}}"/></a:accent2>
      <a:accent3><a:srgbClr val="{{ACCENT3}}"/></a:accent3>
      <a:accent4><a:srgbClr val="{{ACCENT4}}"/></a:accent4>
      <a:accent5><a:srgbClr val="{{ACCENT5}}"/></a:accent5>
      <a:accent6><a:srgbClr val="{{ACCENT6}}"/></a:accent6>
      <a:hlink><a:srgbClr val="0563C1"/></a:hlink>
      <a:folHlink><a:srgbClr val="954F72"/></a:folHlink>
    </a:clrScheme>
    <a:fontScheme name="{{NAME}}">
      <a:majorFont>
        <a:latin typeface="{{MAJOR_FONT}}"/>
        <a:ea typeface=""/>
        <a:cs typeface=""/>
      </a:majorFont>
      <a:minorFont>
        <a:latin typeface="{{MINOR_FONT}}"/>
        <a:ea typeface=""/>
        <a:cs typeface=""/>
      </a:minorFont>
    </a:fontScheme>
    <a:fmtScheme name="Office">
      <a:fillStyleLst>
        <a:solidFill><a:schemeClr val="phClr"/></a:solidFill>
        <a:gradFill rotWithShape="1">
          <a:gsLst>
            <a:gs pos="0"><a:schemeClr val="phClr"><a:lumMod val="110000"/><a:satMod val="105000"/><a:tint val="67000"/></a:schemeClr></a:gs>
            <a:gs pos="50000"><a:schemeClr val="phClr"><a:lumMod val="105000"/><a:satMod val="103000"/><a:tint val="73000"/></a:schemeClr></a:gs>
            <a:gs pos="100000"><a:schemeClr val="phClr"><a:lumMod val="105000"/><a:satMod val="109000"/><a:tint val="81000"/></a:schemeClr></a:gs>
          </a:gsLst>
          <a:lin ang="5400000" scaled="0"/>
        </a:gradFill>
        <a:gradFill rotWithShape="1">
          <a:gsLst>
            <a:gs pos="0"><a:schemeClr val="phClr"><a:satMod val="103000"/><a:lumMod val="102000"/><a:tint val="94000"/></a:schemeClr></a:gs>
            <a:gs pos="50000"><a:schemeClr val="phClr"><a:satMod val="110000"/><a:lumMod val="100000"/><a:shade val="100000"/></a:schemeClr></a:gs>
            <a:gs pos="100000"><a:schemeClr val="phClr"><a:lumMod val="99000"/><a:satMod val="120000"/><a:shade val="78000"/></a:schemeClr></a:gs>
          </a:gsLst>
          <a:lin ang="5400000" scaled="0"/>
        </a:gradFill>
      </a:fillStyleLst>
      <a:lnStyleLst>
        <a:ln w="6350" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/><a:miter lim="800000"/></a:ln>
        <a:ln w="12700" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/><a:miter lim="800000"/></a:ln>
        <a:ln w="19050" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/><a:miter lim="800000"/></a:ln>
      </a:lnStyleLst>
      <a:effectStyleLst>
        <a:effectStyle><a:effectLst/></a:effectStyle>
        <a:effectStyle><a:effectLst/></a:effectStyle>
        <a:effectStyle><a:effectLst><a:outerShdw blurRad="57150" dist="19050" dir="5400000" algn="ctr" rotWithShape="0"><a:srgbClr val="000000"><a:alpha val="63000"/></a:srgbClr></a:outerShdw></a:effectLst></a:effectStyle>
      </a:effectStyleLst>
      <a:bgFillStyleLst>
        <a:solidFill><a:schemeClr val="phClr"/></a:solidFill>
        <a:solidFill><a:schemeClr val="phClr"><a:tint val="95000"/><a:satMod val="170000"/></a:schemeClr></a:solidFill>
        <a:gradFill rotWithShape="1">
          <a:gsLst>
            <a:gs pos="0"><a:schemeClr val="phClr"><a:tint val="93000"/><a:satMod val="150000"/><a:shade val="98000"/><a:lumMod val="102000"/></a:schemeClr></a:gs>
            <a:gs pos="50000"><a:schemeClr val="phClr"><a:tint val="98000"/><a:satMod val="130000"/><a:shade val="90000"/><a:lumMod val="103000"/></a:schemeClr></a:gs>
            <a:gs pos="100000"><a:schemeClr val="phClr"><a:shade val="63000"/><a:satMod val="120000"/></a:schemeClr></a:gs>
          </a:gsLst>
          <a:lin ang="5400000" scaled="0"/>
        </a:gradFill>
      </a:bgFillStyleLst>
    </a:fmtScheme>
  </a:themeElements>
  <a:objectDefaults/>
  <a:extraClrSchemeLst/>
</a:theme>`;

// ─────────────────────────────────────────────────────────────────────
// Color helpers
// ─────────────────────────────────────────────────────────────────────

// Normalize any hex/rgb/rgba string into a 6-char uppercase hex without '#'.
// Returns null for transparent/invalid inputs.
function normalizeHex(input) {
  if (!input || typeof input !== 'string') return null;
  const s = input.trim();
  if (!s || s === 'transparent' || s === 'none' || s === 'inherit') return null;

  // rgb(r, g, b) or rgba(r, g, b, a)
  const rgbMatch = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/i);
  if (rgbMatch) {
    const a = rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1;
    if (a === 0) return null; // fully transparent
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return `${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
  }

  // #rgb or #rrggbb
  const hexMatch = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join('');
    }
    return hex.toUpperCase();
  }

  return null;
}

// Is a color close to pure white or pure black?
function isNearWhite(hex) {
  if (!hex) return false;
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return r > 240 && g > 240 && b > 240;
}
function isNearBlack(hex) {
  if (!hex) return false;
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return r < 25 && g < 25 && b < 25;
}

// ─────────────────────────────────────────────────────────────────────
// DOM extraction (fallback when no brand identity is applied)
// ─────────────────────────────────────────────────────────────────────

// Extract all hex/rgb color tokens from an arbitrary CSS string — used for
// background-image gradients and multi-value properties where colors aren't
// exposed as a single getComputedStyle field.
function extractAllColorsFromCss(str) {
  if (!str) return [];
  const colors = [];
  const rgbMatches = str.match(/rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*[\d.]+\s*)?\)/gi) || [];
  for (const m of rgbMatches) {
    const hex = normalizeHex(m);
    if (hex) colors.push(hex);
  }
  const hexMatches = str.match(/#[0-9a-f]{6}\b|#[0-9a-f]{3}\b/gi) || [];
  for (const m of hexMatches) {
    const hex = normalizeHex(m);
    if (hex) colors.push(hex);
  }
  return colors;
}

const SVG_SHAPE_TAGS = new Set(['svg', 'path', 'circle', 'rect', 'line', 'ellipse', 'polygon', 'polyline']);

// Scan the staged slide DOM for the most prominent colors and fonts.
// Works for any presentation (branded or not) — samples:
//   - solid background colors on every element
//   - CSS gradient stops from backgroundImage (where Gemini puts hero visuals)
//   - SVG fill/stroke attributes on decorative shapes
//   - text colors
//   - border colors
//   - font-family (weighted by font-size to separate heading from body)
//
// Each source is weighted by visual impact: gradients and SVG fills get
// higher weight than text color (which tends to be just black/white).
//
// Returns { accents, hasDark, hasLight, headingFont, bodyFont }.
export function extractTokensFromDom(rootElements) {
  const colorCounts = new Map();
  const fontCounts = new Map();
  const headingFontCounts = new Map();

  const inc = (map, key, weight = 1) => {
    if (!key) return;
    map.set(key, (map.get(key) || 0) + weight);
  };

  for (const root of rootElements) {
    // Include the root wrapper itself — it often holds the hero gradient set
    // by the export pipeline (wrapper.style.background = bodyComputed.background).
    const allEls = [root, ...root.querySelectorAll('*')];

    for (const el of allEls) {
      let cs;
      try { cs = window.getComputedStyle(el); } catch (_) { continue; }
      if (!cs) continue;

      // Solid backgrounds — cards, sections, shapes.
      inc(colorCounts, normalizeHex(cs.backgroundColor), 2);

      // Gradient stops — these are almost always the most visually dominant
      // colors on a Gemini-generated slide (hero backgrounds, accent bars).
      // Weight highest so they beat generic text colors.
      const bgImage = cs.backgroundImage || '';
      if (bgImage && bgImage !== 'none' && bgImage.includes('gradient')) {
        for (const hex of extractAllColorsFromCss(bgImage)) {
          inc(colorCounts, hex, 4);
        }
      }
      // Also check the 'background' shorthand in case the shortcut was set
      // directly (wrapper does this from iframe body).
      const bgShort = el.style?.background;
      if (bgShort && bgShort.includes('gradient')) {
        for (const hex of extractAllColorsFromCss(bgShort)) {
          inc(colorCounts, hex, 4);
        }
      }

      // Text color — included but weighted low since it's often just white/black.
      inc(colorCounts, normalizeHex(cs.color), 1);

      // Border colors — often used as accents.
      inc(colorCounts, normalizeHex(cs.borderTopColor), 2);
      inc(colorCounts, normalizeHex(cs.borderLeftColor), 1);

      // Box-shadow can encode an accent color (e.g. colored glow effects).
      if (cs.boxShadow && cs.boxShadow !== 'none') {
        for (const hex of extractAllColorsFromCss(cs.boxShadow)) {
          inc(colorCounts, hex, 1);
        }
      }

      // SVG shapes — Gemini puts decorative accents (underlines, dots, icons,
      // divider shapes) in inline SVG with fill/stroke attributes. These are
      // visually prominent accents and should rank high.
      const tagLower = (el.tagName || '').toLowerCase();
      if (SVG_SHAPE_TAGS.has(tagLower)) {
        const fillAttr = el.getAttribute && el.getAttribute('fill');
        const strokeAttr = el.getAttribute && el.getAttribute('stroke');
        // getComputedStyle also exposes fill/stroke on SVG elements.
        const fillCs = cs.fill;
        const strokeCs = cs.stroke;
        for (const raw of [fillAttr, strokeAttr, fillCs, strokeCs]) {
          if (!raw || raw === 'none' || raw === 'currentColor') continue;
          const hex = normalizeHex(raw);
          if (hex) inc(colorCounts, hex, 3);
        }
      }

      // Fonts.
      const family = (cs.fontFamily || '').split(',')[0].trim().replace(/["']/g, '');
      if (family && family.toLowerCase() !== 'inherit') {
        inc(fontCounts, family);
        const fontSize = parseFloat(cs.fontSize) || 0;
        if (fontSize >= 32) inc(headingFontCounts, family);
      }
    }
  }

  // Rank colors by weighted count. Drop near-white and near-black from the
  // accent list — those belong in dk1/lt1 slots, not accents.
  const ranked = Array.from(colorCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([hex]) => hex);

  // Deduplicate "visually similar" colors so we don't end up with 6 near-identical
  // blues in the accent palette. Two colors are considered similar if every
  // channel is within 24 points.
  const isSimilar = (a, b) => {
    if (!a || !b) return false;
    const ar = parseInt(a.substr(0, 2), 16), ag = parseInt(a.substr(2, 2), 16), ab = parseInt(a.substr(4, 2), 16);
    const br = parseInt(b.substr(0, 2), 16), bg = parseInt(b.substr(2, 2), 16), bb = parseInt(b.substr(4, 2), 16);
    return Math.abs(ar - br) < 24 && Math.abs(ag - bg) < 24 && Math.abs(ab - bb) < 24;
  };

  const accents = [];
  for (const hex of ranked) {
    if (isNearWhite(hex) || isNearBlack(hex)) continue;
    if (accents.some((a) => isSimilar(a, hex))) continue;
    accents.push(hex);
    if (accents.length >= 6) break;
  }

  const hasDark = ranked.some(isNearBlack);
  const hasLight = ranked.some(isNearWhite);

  const rankedFonts = Array.from(fontCounts.entries()).sort((a, b) => b[1] - a[1]);
  const rankedHeadingFonts = Array.from(headingFontCounts.entries()).sort((a, b) => b[1] - a[1]);

  return {
    accents,
    hasDark,
    hasLight,
    headingFont: rankedHeadingFonts[0]?.[0] || rankedFonts[0]?.[0] || 'Inter',
    bodyFont: rankedFonts[0]?.[0] || 'Inter',
  };
}

// ─────────────────────────────────────────────────────────────────────
// Brand identity fetch
// ─────────────────────────────────────────────────────────────────────

// Fetch a saved brand identity and return { name, colors, fonts } or null.
// Sends the session JWT: the brand GET requires identity since r89.
// Colors is an array of hex strings; fonts is { heading, body }.
export async function fetchBrandTokens(brandIdentityId) {
  if (!brandIdentityId) return null;
  try {
    const res = await fetch(withBackend(`/api/brand-identities/${brandIdentityId}`), { headers: await authHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    const bi = data.brand_identity || data;
    if (!bi) return null;

    const rawColors = bi.colors || bi.brand_colors || [];
    const colors = rawColors
      .map((c) => (typeof c === 'string' ? c : c?.hex || c?.color || ''))
      .map(normalizeHex)
      .filter(Boolean);

    const rawFonts = bi.fonts || bi.brand_fonts || [];
    const fonts = rawFonts
      .map((f) => (typeof f === 'string' ? f : f?.name || f?.family || ''))
      .filter(Boolean);

    return {
      name: bi.name || 'Brand Theme',
      colors,
      headingFont: fonts[0] || null,
      bodyFont: fonts[1] || fonts[0] || null,
    };
  } catch (err) {
    console.warn('[pptxTheme] fetchBrandTokens failed:', err);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Theme XML builder
// ─────────────────────────────────────────────────────────────────────

// Build a filled theme1.xml string from tokens.
// accents: array of up to 6 hex strings (without #). Missing slots are padded.
// dk1/lt1: optional hex overrides for primary text/background.
export function buildThemeXml({
  name = 'Brand Theme',
  accents = [],
  headingFont = 'Inter',
  bodyFont = 'Inter',
  dk1 = '1F2937',
  lt1 = 'FFFFFF',
  dk2 = '374151',
  lt2 = 'F3F4F6',
} = {}) {
  // Pad accents to 6 slots with sensible defaults so PowerPoint gets a full palette.
  const defaults = ['3B82F6', '1E40AF', '60A5FA', '93C5FD', 'F97316', '10B981'];
  const filled = [...accents];
  while (filled.length < 6) filled.push(defaults[filled.length]);

  // Strip '#' and uppercase — defensive, in case a caller forgets to normalize.
  const clean = (hex) => (hex || '').replace(/^#/, '').toUpperCase().padStart(6, '0').slice(0, 6);

  // XML-escape font names (they're attribute values).
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  return THEME_XML_TEMPLATE
    .replace(/\{\{NAME\}\}/g, esc(name))
    .replace('{{DK1}}', clean(dk1))
    .replace('{{LT1}}', clean(lt1))
    .replace('{{DK2}}', clean(dk2))
    .replace('{{LT2}}', clean(lt2))
    .replace('{{ACCENT1}}', clean(filled[0]))
    .replace('{{ACCENT2}}', clean(filled[1]))
    .replace('{{ACCENT3}}', clean(filled[2]))
    .replace('{{ACCENT4}}', clean(filled[3]))
    .replace('{{ACCENT5}}', clean(filled[4]))
    .replace('{{ACCENT6}}', clean(filled[5]))
    .replace('{{MAJOR_FONT}}', esc(headingFont || 'Inter'))
    .replace('{{MINOR_FONT}}', esc(bodyFont || 'Inter'));
}

// ─────────────────────────────────────────────────────────────────────
// Blob injection
// ─────────────────────────────────────────────────────────────────────

// Open a .pptx blob with JSZip, replace ppt/theme/theme1.xml with the given
// theme XML, and return a new blob. If theme1.xml doesn't exist (shouldn't
// happen with dom-to-pptx, but defensively), the original blob is returned.
export async function injectThemeIntoPptx(pptxBlob, themeXml) {
  try {
    const zip = await JSZip.loadAsync(pptxBlob);
    const themePath = 'ppt/theme/theme1.xml';
    if (!zip.file(themePath)) {
      console.warn('[pptxTheme] theme1.xml not found in pptx — skipping injection');
      return pptxBlob;
    }
    zip.file(themePath, themeXml);
    return await zip.generateAsync({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    });
  } catch (err) {
    console.warn('[pptxTheme] injectThemeIntoPptx failed, returning original blob:', err);
    return pptxBlob;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Top-level helper: build theme from brand OR DOM, then inject into blob
// ─────────────────────────────────────────────────────────────────────

// Given the exported pptx blob, the brand identity id (or null), and the
// staging DOM roots (to sample fallback tokens from), return a new blob with
// the theme injected.
export async function applyThemeToExportedPptx({
  pptxBlob,
  brandIdentityId,
  slideRoots,
  presentationTitle,
}) {
  const brand = await fetchBrandTokens(brandIdentityId);
  const domTokens = slideRoots && slideRoots.length ? extractTokensFromDom(slideRoots) : null;

  // Prefer brand tokens when present; fall back to DOM-sampled tokens.
  const accents = (brand && brand.colors.length > 0) ? brand.colors : (domTokens?.accents || []);
  const headingFont = brand?.headingFont || domTokens?.headingFont || 'Inter';
  const bodyFont = brand?.bodyFont || domTokens?.bodyFont || 'Inter';
  const themeName = brand?.name || presentationTitle || 'Brand Theme';

  const themeXml = buildThemeXml({
    name: themeName,
    accents,
    headingFont,
    bodyFont,
  });

  return await injectThemeIntoPptx(pptxBlob, themeXml);
}
